import {
  cameraProjectionMatrixInverse,
  cameraWorldMatrix,
  getViewPosition,
  screenUV,
  texture,
  vec4,
} from 'three/tsl';
import * as THREE from 'three/webgpu';

type N = any;

/**
 * Deferred light volumes: soft point lights (lantern stalks, crystals) drawn as
 * spheres in a separate pass that reads the main pass's depth, so their light
 * lands on whatever surface is inside the sphere: ground, grass, rocks.
 *
 * The depth texture belongs to the scene pass (SceneSurface), shared by every
 * sky's pipeline. useVolumeDepth() points them at it; swapping it later also
 * rebuilds the volume materials (their texture binding is fixed when they compile).
 */
export const volumeDepth: N = texture(new THREE.DepthTexture(1, 1));
const materials: THREE.Material[] = [];

/** Register a material that reads volumeDepth (rebuilt when the depth source changes). */
export function registerVolumeMaterial(m: THREE.Material): void {
  materials.push(m);
}

/** Point the light volumes at a pipeline's scene depth. Cheap when it's already the one. */
export function useVolumeDepth(depth: THREE.Texture): void {
  if (volumeDepth.value === depth) return;
  volumeDepth.value = depth;
  for (const m of materials) m.needsUpdate = true;
}

/** World position (camera-relative) of the scene surface behind the current fragment. */
export function sceneSurfacePosition(): N {
  const depth = volumeDepth.sample(screenUV).r;
  const view = getViewPosition(screenUV, depth, cameraProjectionMatrixInverse);
  return cameraWorldMatrix.mul(vec4(view, 1)).xyz;
}
